/* eslint-disable sonarjs/cognitive-complexity */
import { useEffect, useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { toCamel } from 'convert-keys'

import { V2ConfigurationContext } from 'contexts/Configuration'

import { getIframeURL, getHostPageUrl, isValidIntersectionEntry } from 'helper'
import { useAnalytics, EventName, BroadcastEventName, getUTMs } from 'analytics'
import { postBroadcastMessage, buildBroadcastMessage } from 'messenger'
import { withAnalyticsConsent } from 'consent'

import { Body4, DEFAULT_FONT_COLOR } from 'Text'

import ArrowIcon from 'images/arrow-white.svg'

import Header from './Header'
import PoweredBy from './PoweredBy'

const NON_SUPPORTED_GROCERS = ['kroger', 'instacart_']

const getGrocersFromWindow = () => {
  if (!window.props || !window.props?.grocers) return []
  return toCamel(window.props.grocers)
}

// onClick is used in 'View All' button
const MultiStyleButton = ({ recipeData, onClick, onImpression }) => {
  const buttonRef = useRef(null)
  const analytics = useAnalytics()
  const {
    disableViewAll, partner, utmSource, utmCampaign, utmMedium, grocers: enabledGrocers,
    shoppablelinkId, buttonScale
  } = useContext(V2ConfigurationContext)

  const grocers = recipeData.grocers || getGrocersFromWindow()

  if (!grocers || grocers.length === 0) return null

  const enabledGrocersList = enabledGrocers ? enabledGrocers.split(',') : []
  const partnerGrocers = grocers.filter(grocer => {
    const isSupported = !NON_SUPPORTED_GROCERS.includes(grocer.source) &&
      !NON_SUPPORTED_GROCERS.some(prefix => grocer.name.startsWith(prefix))
    if (!isSupported) return false
    return true
  })

  let shownGrocers = partnerGrocers.slice(0, 3)
  // If enabled grocers are provided, the grocer order should be followed by the enabled grocers
  if (enabledGrocersList.length > 0) {
    shownGrocers = []
    enabledGrocersList.forEach(grocer => {
      const partnerGrocer = partnerGrocers.find(partnerGrocer => partnerGrocer.name === grocer)
      if (partnerGrocer) {
        shownGrocers.push(partnerGrocer)
      }
    })
    shownGrocers = shownGrocers.slice(0, 3)
  }

  const buttonCount = shownGrocers.length + (disableViewAll ? 0 : 1)

  let displayedEventSended = false
  let displayedEventCount = 0

  // Setup IntersectionObserver to send impression event
  useEffect(() => {
    let observer = null
    if (buttonRef?.current) {
      // The IntersectionObserver should add on content of shadow DOM, not the root element.
      // Otherwise, the callback will not be called
      observer = new IntersectionObserver(onIntersection, { threshold: 1 })
      observer.observe(buttonRef.current)
    }
    return () => {
      if (observer) observer.disconnect()
    }
  }, [buttonRef, analytics])

  const onIntersection = entries => {
    entries.forEach(entry => {
      if (displayedEventSended || !isValidIntersectionEntry(entry)) return
      displayedEventSended = true
      analytics.sendEvent(EventName.RETAILER_DISPLAYED, {
        name_list: shownGrocers.map(grocer => grocer.name),
        referrer_name: 'widget',
        keyword: getUTMs(),
        count: displayedEventCount
      }, partner)
      displayedEventCount += 1
    })
    
    if (onImpression) onImpression(entries)
  }

  const onDirectButtonClick = grocer => {
    if (!grocer) return

    const params = new URLSearchParams({
      lang: 'en-us',
      // To distinguish it from widget popup,
      // here we do need to use the 'widget' as referrer name
      referrer_name: 'widget',
      exp_name: 'multi-button',
      partner,
      grocer: grocer.name,
      referrer: getHostPageUrl()
    })

    const uuid = analytics.getUUID()
    if (utmSource) params.append('utm_source', utmSource)
    if (utmCampaign) params.append('utm_campaign', utmCampaign)
    if (utmMedium) params.append('utm_medium', utmMedium)
    if (uuid) params.append('uuid', uuid)
    if (shoppablelinkId) params.append('link_raw_id', shoppablelinkId)

    window.open(withAnalyticsConsent(getIframeURL(partner, `/v3/widget/recipes/${recipeData.id}/cart/add_recipes/?${params.toString()}`)))

    analytics.sendEvent(EventName.RETAILER_CLICKED, {
      destination: grocer.name,
      referrer_name: 'widget-direct'
    })

    postBroadcastMessage(buildBroadcastMessage(BroadcastEventName.ADD_TO_CART, { retailer: grocer.name, eventType: 'Direct' }))
  }

  return (
    <Container ref={buttonRef} $buttonScale={buttonScale}>
      <Header />
      <GrocersContainer $buttonCount={buttonCount}>
        {shownGrocers.map(grocer => (
          <GrocerButton key={grocer.name} grocer={grocer} onClick={onDirectButtonClick} />
        ))}
        {!disableViewAll && <ViewAllButton onClick={onClick} />}
      </GrocersContainer>
      <PoweredBy />
    </Container>
  )
}

const GrocerButton = ({ grocer, onClick }) => (
  <ButtonContainer onClick={() => onClick(grocer)} data-testid="shop button">
    <GrocerIconContainer>
      <GrocerIcon src={grocer.icon} alt={`${grocer.displayName} icon`} />
    </GrocerIconContainer>
    <GrocerNameText>
      {grocer.displayName}
    </GrocerNameText>
  </ButtonContainer>
)

const ViewAllButton = ({ onClick }) => {
  const { t } = useTranslation()
  return (
    <ButtonContainer onClick={onClick} data-testid="view all button">
      <ViewAllButtonIcon>
        <ArrowIcon style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} alt="arrow icon" />
      </ViewAllButtonIcon>
      <GrocerNameText>{t('viewAll')}</GrocerNameText>
    </ButtonContainer>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 310px;
  width: fit-content;
  transform: scale(clamp(0.5, calc(100vw / 310px), ${({ $buttonScale }) => $buttonScale ?? 1}));
  transform-origin: left top;
`

const GrocersContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $buttonCount = 4 }) => $buttonCount}, 72px);
  gap: var(--sc-widget-button-gap, 4px);
  padding: 4px 2px;
`

const ButtonContainer = styled.button`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  border: none;
  background: none;
`

const ButtonIconBase = styled.div`
  width: 64px;
  height: 64px;
  border-radius: var(--sc-widget-button-border-radius, 50%);
  position: relative;
`

const ViewAllButtonIcon = styled(ButtonIconBase)`
  background-color: var(--sc-widget-button-background-color, #000000);
`

const GrocerIconContainer = styled(ButtonIconBase)`
  border: 1px solid #F5F5F5;
  box-sizing: border-box;
  margin-top: 0;
  &:hover {
    border: 2px solid #F5F5F5;
    box-shadow: 0px 2px 16px rgba(0, 0, 0, 0.12);
  }
`

const GrocerIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const GrocerNameText = styled(Body4)`
  color: var(--sc-widget-button-text-color, ${DEFAULT_FONT_COLOR});
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  width: 100%;
  text-wrap: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`

export default MultiStyleButton
